import React from 'react'
import { ThemeProvider } from 'styled-components'

import keuzehulpen, { Keuzehulp } from '../keuzehulpen'
import theme from '../theme'
import { AnswerStorageType, ConditionalRouteType, Slide } from '../types'

const withTheme = (children: JSX.Element) => <ThemeProvider theme={theme}>{children}</ThemeProvider>

const getKeuzehulp = (basePath: string) => {
  const keuzehulp: Keuzehulp | undefined = keuzehulpen[basePath]

  if (keuzehulp) {
    const routes = keuzehulp.routes.map(route => ({
      ...route,
      path: `${keuzehulp.basePath}${route.path}`
    }))
    return { routes, title: keuzehulp.title, documentTitle: keuzehulp.documentTitle }
  }
  return { routes: [], title: '', documentTitle: '' }
}

const getLocalStorage = (name: string): AnswerStorageType[] => {
  if (localStorage.getItem(name)) {
    const initValue = localStorage.getItem(name)
    return initValue ? JSON.parse(initValue).value : []
  }

  return []
}

const getSkipRouteStorage = (): string[] => {
  if (localStorage.getItem('skipChapters')) {
    const initValue = localStorage.getItem('skipChapters')
    return initValue ? JSON.parse(initValue).chapters : []
  }

  return []
}

const getPivotLocalStorage = (name: string): ConditionalRouteType[] => {
  if (localStorage.getItem(name)) {
    const initValue = localStorage.getItem(name) || ''
    return initValue ? JSON.parse(initValue).value : []
  }

  return []
}

const sortLocalStorage = (
  storageArray: AnswerStorageType[],
  basePath: string
): AnswerStorageType[] => {
  const keuzehulp: Keuzehulp | undefined = keuzehulpen[basePath]
  const routes = keuzehulp ? keuzehulp.routes : []
  let storage = [...storageArray]
  const sortedStorage: AnswerStorageType[] = []

  routes.forEach(route => {
    let found = false
    storage = storage.filter(item => {
      if (route.type === 'chapter') {
        item.chapter = route.routeId
      }

      if (!found && item.routeId === route.routeId) {
        sortedStorage.push(item)
        found = true
        return false
      } else return true
    })
  })

  return sortedStorage
}

const handleRemoveLocalStorage = (
  storageArray: AnswerStorageType[],
  routeId: string
): AnswerStorageType[] => {
  const foundIndex = storageArray.findIndex(item => item.routeId === routeId)
  const spliced = storageArray.splice(0, foundIndex + 1)
  return spliced
}

const setLocalStorage = (answer: AnswerStorageType, basePath: string) => {
  const storage: AnswerStorageType[] = getLocalStorage('answers')
  let newStorage = storage.length > 0 ? storage : []
  const foundIndex = newStorage.findIndex(item => item.routeId === answer.routeId)

  if (foundIndex < 0) {
    newStorage = [...newStorage, answer]
  } else {
    newStorage[foundIndex] = answer
  }

  let sortedStorage = sortLocalStorage(newStorage, basePath)

  if (answer.isConditionalRoute)
    sortedStorage = handleRemoveLocalStorage(sortedStorage, answer.routeId)

  localStorage.setItem('updatedAt', `${new Date()}`)

  localStorage.setItem(
    'answers',
    JSON.stringify({
      value: sortedStorage
    })
  )
}

const setSkipRouteStorage = (skipChapterList: string[]) => {
  localStorage.setItem(
    'skipChapters',
    JSON.stringify({
      chapters: skipChapterList
    })
  )
}

const dvhIsSupported = (): boolean => {
  const dvhSupported = CSS.supports('height', '100dvh')

  return dvhSupported
}

const savePivotPoint = (pivot: ConditionalRouteType) => {
  const storage: ConditionalRouteType[] = getPivotLocalStorage('pivotPoints')
  let newStorage = storage.length > 0 ? storage : []
  const foundIndex = newStorage.findIndex(item => item.pivotRouteId === pivot.pivotRouteId)

  if (foundIndex < 0) {
    newStorage = [...newStorage, pivot]
  } else {
    newStorage[foundIndex] = pivot
  }

  localStorage.setItem(
    'pivotPoints',
    JSON.stringify({
      value: newStorage
    })
  )
}

const setPivotPoint = (pivotPoint: ConditionalRouteType[] | ConditionalRouteType) => {
  if (pivotPoint instanceof Array) {
    pivotPoint.map((item: ConditionalRouteType) => savePivotPoint(item))
  } else {
    savePivotPoint(pivotPoint)
  }
}

const getSecondOccurrenceOfSubstring = (str: string, substring: string): number => {
  const firstIndex = str.indexOf(substring)
  return str.indexOf(substring, firstIndex + 1)
}

const cutOffRouteIndexToTwoDigits = (routeIndex: string): string => {
  const secondOccurrenceOfDot = getSecondOccurrenceOfSubstring(routeIndex, '.')

  if (secondOccurrenceOfDot === -1) {
    return routeIndex
  }

  return routeIndex.substring(0, secondOccurrenceOfDot)
}

const getNextSkipRoute = (routeIndex: string, routes: Slide[], isChapter = false) => {
  const skipRouteStorage = getSkipRouteStorage()
  const shouldSkipRoute = skipRouteStorage.some(
    route => route === cutOffRouteIndexToTwoDigits(routeIndex)
  )
  const chapterIndex = parseInt(routeIndex.split('.')[0], 10)
  const subSlideIndex = parseInt(routeIndex.split('.')[1], 10)

  if (skipRouteStorage && shouldSkipRoute) {
    const increment = 1
    let nextIndex = ''

    skipRouteStorage.forEach(() => {
      if (isChapter) {
        nextIndex = (chapterIndex + increment).toString()
      } else {
        nextIndex = `${chapterIndex}.${subSlideIndex + increment}`.toString()
      }
    })

    const nextRoute = routes.find(route => route.routeIndex === nextIndex.toString())

    if (nextRoute) {
      return nextRoute
    } else {
      const currentRoute = routes.find(route => route.routeIndex === routeIndex)

      if (currentRoute) {
        return routes.find(route => route.routeId === currentRoute.nextRouteId)
      }
    }
  }
}

export {
  withTheme,
  getLocalStorage,
  setLocalStorage,
  setSkipRouteStorage,
  getSkipRouteStorage,
  getKeuzehulp,
  dvhIsSupported,
  setPivotPoint,
  getPivotLocalStorage,
  getNextSkipRoute
}
